import i18n from "i18next";

import axios from "axios";
import {
  fixedFormatter,
  twoDecimalsFormatter,
  percent100Formatter,
  percent100twoDecimalsFormatter,
  fourDecimalsFormatter,
  addExportButton,
} from "../util/tableUtils";
import { operationCancelled } from "../../lib/httpClient";
import { getValueToIconMapForHazard } from "../map/HazardSelectorHelper";
import { CysButtonPopover } from "../util/CysButtonPopover";
import httpClient from "../../lib/httpClient";
import { Grid } from "@material-ui/core";
import { filterModelToQueryValue } from "../util/tableUtils";

import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { Box } from "@material-ui/core";

export const aggregatedAdministrativeTableColumns = () => [
  // These columns are common for all aggregated tables
  {
    field: "n_pol",
    headerName: i18n.t("climateAnalytics.policies"),
    width: 60,
    ...fixedFormatter,
  },

  {
    field: "n_sx",
    headerName: i18n.t("climateAnalytics.NUMERO_SINISTRI"),
    width: 60,
    ...fixedFormatter,
  },
  {
    field: "ValoreAssicurato",
    headerName: i18n.t("agriculture.insuredValue"),
    ...twoDecimalsFormatter,
  },

  {
    field: "RisarcimentoTotale",
    headerName: i18n.t("agriculture.claims.totalCompensation"),
  },

  {
    field: "PremioNetto",
    headerName: i18n.t("climateAnalyticsSelects.premiumNettoEur"),
    ...twoDecimalsFormatter,
  },

  {
    field: "incidenza",
    headerName: i18n.t("climateAnalytics.incidence"),
    ...percent100twoDecimalsFormatter,
  },
  {
    field: "costo_medio",
    headerName: i18n.t("climateAnalytics.COSTO_MEDIO"),
    ...fixedFormatter,
  },
  {
    field: "grado_medio_danno",
    headerName: i18n.t("climateAnalytics.GRADO_MEDIO_DI_DANNO"),
    ...fourDecimalsFormatter,
  },

  {
    field: "tasso_obs",
    headerName: i18n.t("climateAnalytics.TASSO_OBS"),
    ...fourDecimalsFormatter,
  },
  {
    field: "tasso_pagato",
    headerName: i18n.t("climateAnalytics.TASSO_PAGATO"),
    ...fourDecimalsFormatter,
  },

  {
    field: "premio_puro_obs",
    headerName: i18n.t("climateAnalytics.PREMIO_PURO_OBS"),
    ...twoDecimalsFormatter,
  },

  {
    field: "premio_medio",
    headerName: i18n.t("climateAnalytics.PREMIO_MEDIO"),
    ...twoDecimalsFormatter,
  },

  {
    field: "TIV_medio",
    headerName: i18n.t("climateAnalytics.IMPORTO_MEDIO_VALORE_ASSICURATO_INDICIZZATO"),
    ...fixedFormatter,
  },

  {
    field: "LR",
    headerName: "LR",
    ...percent100Formatter,
  },
];

export const getAggregatedDataWrapper = async (aggregatedCancelTokenRef, getAggregatedData) => {
  // Cancel the previous request when the filterModel changes
  if (aggregatedCancelTokenRef.current) {
    aggregatedCancelTokenRef.current.cancel(operationCancelled);
  }

  // Create a new cancel token for the current request
  aggregatedCancelTokenRef.current = axios.CancelToken.source();
  getAggregatedData();
};

export const getAggregatedData = async (
  setAggregatedTableState,
  setAggregatedTableData,
  aggrBy,

  filterModel,
  wantedReferenceDateProp,
  aggregatedCancelTokenRef,
) => {
  setAggregatedTableState((old) => ({ ...old, isLoading: true }));
  const load = {
    ...aggrBy,
    refDate: wantedReferenceDateProp,
    filters: filterModelToQueryValue(filterModel),
  };
  try {
    const response = await httpClient().post(`proxy/agroanalytics_reale`, load, {
      cancelToken: aggregatedCancelTokenRef.current.token,
    });

    // If you print response here, it looks like it is already processed by addTitlesForRows()

    if (response?.data?.error) {
      alert("You encountered an internal error, please try again later." + response?.data?.error);
      setAggregatedTableData([{ id: 0 }]);
    } else {
      setAggregatedTableData(response?.data?.plainResult);
    }

    setAggregatedTableState((old) => ({
      ...old,
      isLoading: false,
    }));
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      console.error("Error:", error, "Load:", load);
    }
  }
};

const explainedColumns = (aggregatedTableColumns) => {
  return (
    <Box m={1}>
      <ul>
        {aggregatedTableColumns.map((column) => (
          <li key={column.field}>
            <b>{column.headerName}</b>
            {i18n.exists(`agroAnalyticColumn.${column.field}`)
              ? " : " + i18n.t(`agroAnalyticColumn.${column.field}`)
              : ""}
          </li>
        ))}
      </ul>
    </Box>
  );
};

export const onFilterModelChange = (filterModel, getAggregatedDataWrapper) => {
  if (filterModel) {
    const isAnyIncompleteFilter = filterModel?.items?.some((filter) => !filter?.value);

    if (!isAnyIncompleteFilter) {
      getAggregatedDataWrapper();
    }
  } else {
    getAggregatedDataWrapper();
  }
};

export const renderGrid = (
  aggregatedTableColumns,
  aggregatedTableData,
  aggregatedTableState,
  t,
  fileNamePrefix,
  props,
) => {
  return aggregatedTableData.length > -1 ? (
    <Grid item xs={12} sm={12}>
      <div style={{ width: "100%" }}>
        <DataGridCystellarTable
          density={"compact"}
          pageSizeOptions={[5, 25, 50, 100]}
          columns={aggregatedTableColumns}
          rows={aggregatedTableData}
          loading={aggregatedTableState.isLoading}
          customToolbarComp={true}
          customToolbarWidgets={
            <>
              {addExportButton(fileNamePrefix)}
              <CysButtonPopover
                buttonMsg={i18n.t("analyticsLegend.descriptionOfColumns")}
                popoverText={explainedColumns(aggregatedTableColumns)}
              ></CysButtonPopover>
            </>
          }
          {...props}
        />
      </div>
    </Grid>
  ) : null;
};

export const headerForHazard = (hazardSelect) => {
  if (hazardSelect === "anyhazard") {
    return i18n.t("climateAnalyticsSelects.anyHazard");
  } else {
    return getValueToIconMapForHazard(hazardSelect).text;
  }
};

export const headerForKPI = (KPISelect) => {
  if (KPISelect === "none") {
    return i18n.t("climateAnalyticsSelects.numOfMunicipalities");
  } else {
    return i18n.t("climateAnalyticsSelects." + KPISelect);
  }
};
